/**
 * Client-Side Job Crawler
 * Fetches REAL ADA/accessibility job listings from public APIs and job boards
 * NO synthetic or fabricated data — empty fields for missing info
 */

// Strip HTML tags from API responses
const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, ' ').replace(/&[^;]+;/g, ' ').replace(/\s+/g, ' ').trim();
};

// Multiple CORS proxies with fallback
const CORS_PROXIES = [
  'https://api.allorigins.win/raw?url=',
  'https://corsproxy.io/?',
  'https://api.codetabs.com/v1/proxy?quest=',
  'https://thingproxy.freeboard.io/fetch/'
];

// Target job sites (ADA/accessibility focused)
const JOB_SOURCES = {
  indeed: {
    name: 'Indeed',
    baseUrl: 'https://www.indeed.com/jobs?q=ada+compliance+specialist',
    parser: 'parseIndeed'
  },
  linkedin: {
    name: 'LinkedIn',
    baseUrl: 'https://www.linkedin.com/jobs/search/?keywords=accessibility%20specialist',
    parser: 'parseLinkedIn'
  },
  usajobs: {
    name: 'USAJobs',
    baseUrl: 'https://www.usajobs.gov/Search/Results?k=accessibility',
    parser: 'parseUSAJobs'
  }
};

/**
 * Helper to build a URL from a base and options such as country or date range.
 * Currently supports switching domains for India vs USA and adding location query params.
 * @param {string} baseUrl - Base URL for the job source
 * @param {string} sourceName - Name of the source (indeed, linkedin, usajobs)
 * @param {string} country - Single country string ('USA' or 'India')
 * @param {object} options - Additional options like startDate, endDate
 */
const buildCrawlUrl = (baseUrl, sourceName, country, options = {}) => {
  let url = baseUrl;

  if (country) {
    const countryLower = String(country).toLowerCase();
    console.log(`🔗 Building URL for source: ${sourceName}, country: ${country}`);
    
    if (sourceName === 'indeed') {
      // switch to india domain if requested
      if (countryLower === 'india') {
        url = url.replace('indeed.com', 'indeed.co.in');
      }
      // append location parameter
      const loc = countryLower === 'india' ? 'India' : 'United States';
      url += url.includes('?') ? `&l=${encodeURIComponent(loc)}` : `?l=${encodeURIComponent(loc)}`;
    } else if (sourceName === 'linkedin') {
      const loc = countryLower === 'india' ? 'India' : 'United States';
      url += url.includes('?') ? `&location=${encodeURIComponent(loc)}` : `?location=${encodeURIComponent(loc)}`;
    } else if (sourceName === 'usajobs') {
      // USAJobs only covers United States; no change
      if (countryLower !== 'usa') {
        console.warn(`⚠️ USAJobs only covers USA, skipping for ${country}`);
      }
    }
  }

  // basic date filter: Indeed supports 'fromage' days ago; we compute from startDate if given
  if (options.startDate) {
    try {
      const start = new Date(options.startDate);
      const now = new Date();
      const diffDays = Math.ceil((now - start) / (1000 * 60 * 60 * 24));
      url += url.includes('?') ? `&fromage=${diffDays}` : `?fromage=${diffDays}`;
    } catch (e) {
      console.warn('Invalid startDate for crawl options', options.startDate);
    }
  }

  // endDate not directly supported by simple search - ignored for now
  console.log(`🌐 Final URL: ${url}`);
  return url;
};

/**
 * Fetch HTML content via CORS proxies
 */
const fetchViaProxy = async (url) => {
  let lastError = null;
  
  for (let i = 0; i < CORS_PROXIES.length; i++) {
    const proxyUrl = CORS_PROXIES[i] + encodeURIComponent(url);
    
    try {
      console.log(`🌐 Trying proxy ${i + 1}/${CORS_PROXIES.length}: ${CORS_PROXIES[i].split('?')[0]}...`);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15s timeout
      
      const response = await fetch(proxyUrl, {
        signal: controller.signal,
        headers: {
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
        }
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const html = await response.text();
      
      if (html && html.length > 500) {
        console.log(`✅ Success with proxy ${i + 1}: ${html.length} bytes`);
        return { html, proxy: CORS_PROXIES[i] };
      }
    } catch (error) {
      lastError = error;
      console.log(`❌ Proxy ${i + 1} failed:`, error.message);
      continue;
    }
  }
  
  throw new Error(`All proxies failed. Last error: ${lastError?.message}`);
};

/**
 * Fetch JSON from a URL — try direct fetch first then CORS proxies
 */
const fetchJSON = async (url) => {
  // Try direct fetch (works if API has CORS headers)
  try {
    const controller = new AbortController();
    const tid = setTimeout(() => controller.abort(), 12000);
    const resp = await fetch(url, { signal: controller.signal });
    clearTimeout(tid);
    if (resp.ok) {
      const data = await resp.json();
      console.log(`✅ Direct JSON fetch succeeded: ${url.substring(0, 80)}`);
      return data;
    }
  } catch (e) {
    console.log(`⚠️ Direct fetch failed for ${url.substring(0, 60)}, trying proxies...`);
  }

  // Try via CORS proxies
  for (let i = 0; i < CORS_PROXIES.length; i++) {
    try {
      const controller = new AbortController();
      const tid = setTimeout(() => controller.abort(), 15000);
      const resp = await fetch(CORS_PROXIES[i] + encodeURIComponent(url), {
        signal: controller.signal
      });
      clearTimeout(tid);
      if (resp.ok) {
        const text = await resp.text();
        const data = JSON.parse(text);
        console.log(`✅ JSON via proxy ${i + 1} succeeded`);
        return data;
      }
    } catch (e) { continue; }
  }

  throw new Error(`All fetch attempts failed for ${url}`);
};

// Accessibility-related keywords for client-side filtering
const ACCESSIBILITY_KEYWORDS = [
  'access', 'wcag', 'ada ', 'a11y', 'compliance', 'inclusive',
  'disability', 'assistive', '508', 'screen reader', 'aria',
  'remediation', 'usability', 'ux research', 'design system'
];

const matchesAccessibility = (job, textFields = []) => {
  const combined = textFields.map(f => String(f || '').toLowerCase()).join(' ');
  return ACCESSIBILITY_KEYWORDS.some(kw => combined.includes(kw));
};

const filterByCountry = (jobs, country, locFn) => {
  if (!country) return jobs;
  const cl = String(country).toLowerCase();
  return jobs.filter(j => {
    const loc = String(locFn(j) || '').toLowerCase();
    if (cl === 'india') {
      return loc.includes('india') || loc.includes('anywhere') || loc.includes('worldwide') || loc === '';
    } else if (cl === 'usa') {
      return loc.includes('usa') || loc.includes('united states') || loc.includes('us only')
        || loc.includes('north america') || loc.includes('anywhere') || loc.includes('worldwide') || loc === '';
    }
    return true;
  });
};

const filterByDateRange = (jobs, options, dateFn) => {
  let result = jobs;
  if (options.startDate) {
    const start = new Date(options.startDate);
    result = result.filter(j => { const d = dateFn(j); return d && new Date(d) >= start; });
  }
  if (options.endDate) {
    const end = new Date(options.endDate);
    result = result.filter(j => { const d = dateFn(j); return d && new Date(d) <= end; });
  }
  return result;
};

/**
 * SOURCE 1: Remotive API (free, CORS-friendly, real remote job listings)
 * NOTE: search= param returns 0 for niche terms — fetch ALL and filter locally
 */
const crawlRemotiveAPI = async (country, options = {}) => {
  console.log('📡 Remotive: Fetching all jobs (search param broken, filtering locally)...');

  let allRawJobs = [];
  try {
    const data = await fetchJSON('https://remotive.com/api/remote-jobs');
    if (data && Array.isArray(data.jobs)) {
      allRawJobs = data.jobs;
      console.log(`📦 Remotive: ${allRawJobs.length} total jobs fetched`);
    }
  } catch (e) {
    console.error('❌ Remotive API failed:', e.message);
    return [];
  }

  // Filter for accessibility-related jobs
  let filtered = allRawJobs.filter(j =>
    matchesAccessibility(j, [j.title, j.description, (j.tags || []).join(' '), j.category])
  );
  console.log(`🔍 Remotive: ${filtered.length} accessibility-related jobs`);

  // Filter by country
  filtered = filterByCountry(filtered, country, j => j.candidate_required_location);

  // Filter by date range
  filtered = filterByDateRange(filtered, options, j => j.publication_date);

  console.log(`📦 Remotive: ${filtered.length} jobs after country/date filter (${country || 'all'})`);

  return filtered.map(j => ({
    id: `remotive-${j.id}`,
    title: j.title || '',
    company: j.company_name || '',
    location: j.candidate_required_location || '',
    description: stripHtml(j.description || '').substring(0, 1500),
    type: j.job_type ? j.job_type.replace(/_/g, ' ') : '',
    experience: '',
    salary: j.salary || '',
    contactEmail: '',
    company_website: '',
    adaRequirements: [],
    skills: Array.isArray(j.tags) ? j.tags : [],
    industry: j.category || '',
    posted_date: j.publication_date ? j.publication_date.split('T')[0] : '',
    source: 'Remotive',
    apply_url: j.url || ''
  }));
};

/**
 * SOURCE 2: The Muse API (free, no key, 9600+ real jobs, USA + India)
 * Fetches multiple categories and pages, filters for accessibility terms
 */
const crawlTheMuseAPI = async (country, options = {}) => {
  const categories = ['Software Engineering', 'Design and UX', 'Data and Analytics', 'Project Management'];
  const allRawJobs = [];

  for (const cat of categories) {
    // Fetch first 3 pages per category (60 jobs)
    for (let page = 0; page < 3; page++) {
      try {
        const url = `https://www.themuse.com/api/public/jobs?page=${page}&category=${encodeURIComponent(cat)}&descending=true`;
        const data = await fetchJSON(url);
        if (data && Array.isArray(data.results)) {
          allRawJobs.push(...data.results);
        }
        if (!data.results || data.results.length < 20) break; // No more pages
      } catch (e) {
        console.warn(`The Muse "${cat}" page ${page} failed:`, e.message);
        break;
      }
      await new Promise(r => setTimeout(r, 300));
    }
  }

  // Deduplicate by Muse job ID
  const uniqueMap = new Map();
  allRawJobs.forEach(j => { if (j.id) uniqueMap.set(j.id, j); });
  let unique = [...uniqueMap.values()];
  console.log(`📦 The Muse: ${unique.length} unique jobs fetched`);

  // Filter for accessibility-related
  let filtered = unique.filter(j =>
    matchesAccessibility(j, [j.name, j.contents, j.company?.name, (j.categories || []).map(c => c.name).join(' ')])
  );
  console.log(`🔍 The Muse: ${filtered.length} accessibility-related jobs`);

  // Filter by country (The Muse uses location objects)
  if (country) {
    const cl = String(country).toLowerCase();
    filtered = filtered.filter(j => {
      const locs = (j.locations || []).map(l => (l.name || '').toLowerCase()).join(', ');
      if (cl === 'india') {
        return locs.includes('india') || locs.includes('flexible') || locs.includes('remote') || locs === '';
      } else if (cl === 'usa') {
        // Most Muse jobs are USA-based; include if has USA locations or remote
        return !locs.includes('india') && !locs.includes('europe') && !locs.includes('uk')
          || locs.includes('flexible') || locs.includes('remote') || locs === '';
      }
      return true;
    });
  }

  // Filter by date range
  filtered = filterByDateRange(filtered, options, j => j.publication_date);

  console.log(`📦 The Muse: ${filtered.length} jobs after country/date filter (${country || 'all'})`);

  return filtered.map(j => ({
    id: `muse-${j.id}`,
    title: j.name || '',
    company: j.company?.name || '',
    location: (j.locations || []).map(l => l.name).join(', ') || '',
    description: stripHtml(j.contents || '').substring(0, 1500),
    type: '',
    experience: (j.levels || []).map(l => l.name).join(', ') || '',
    salary: '',
    contactEmail: '',
    company_website: '',
    adaRequirements: [],
    skills: (j.categories || []).map(c => c.name) || [],
    industry: (j.categories || []).map(c => c.name).join(', ') || '',
    posted_date: j.publication_date ? j.publication_date.split('T')[0] : '',
    source: 'The Muse',
    apply_url: j.refs?.landing_page || ''
  }));
};

/**
 * SOURCE 3: Arbeitnow API (free, no key, 100+ real jobs per page)
 * Good for remote/international jobs
 */
const crawlArbeitnowAPI = async (country, options = {}) => {
  let allRawJobs = [];

  try {
    const data = await fetchJSON('https://www.arbeitnow.com/api/job-board-api');
    if (data && Array.isArray(data.data)) {
      allRawJobs = data.data;
      console.log(`📦 Arbeitnow: ${allRawJobs.length} total jobs fetched`);
    }
  } catch (e) {
    console.error('❌ Arbeitnow API failed:', e.message);
    return [];
  }

  // Filter for accessibility-related
  let filtered = allRawJobs.filter(j =>
    matchesAccessibility(j, [j.title, j.description, (j.tags || []).join(' '), j.company_name])
  );
  console.log(`🔍 Arbeitnow: ${filtered.length} accessibility-related jobs`);

  // Filter by country
  filtered = filterByCountry(filtered, country, j => j.location);

  // Filter by date range (created_at is a unix timestamp)
  if (options.startDate) {
    const start = new Date(options.startDate).getTime() / 1000;
    filtered = filtered.filter(j => j.created_at && j.created_at >= start);
  }
  if (options.endDate) {
    const end = new Date(options.endDate).getTime() / 1000;
    filtered = filtered.filter(j => j.created_at && j.created_at <= end);
  }

  console.log(`📦 Arbeitnow: ${filtered.length} jobs after country/date filter (${country || 'all'})`);

  return filtered.map(j => ({
    id: `arbeitnow-${j.slug || Date.now()}-${Math.random().toString(36).slice(2,6)}`,
    title: j.title || '',
    company: j.company_name || '',
    location: j.location || '',
    description: stripHtml(j.description || '').substring(0, 1500),
    type: j.remote ? 'Remote' : '',
    experience: '',
    salary: '',
    contactEmail: '',
    company_website: '',
    adaRequirements: [],
    skills: Array.isArray(j.tags) ? j.tags : [],
    industry: '',
    posted_date: j.created_at ? new Date(j.created_at * 1000).toISOString().split('T')[0] : '',
    source: 'Arbeitnow',
    apply_url: j.url || ''
  }));
};

/**
 * Parse Indeed job listings — REAL data only, no fabricated fields
 */
const parseIndeed = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const jobs = [];
  
  // Indeed job cards
  const jobCards = doc.querySelectorAll('.job_seen_beacon, .jobsearch-SerpJobCard, [data-jk]');
  
  jobCards.forEach((card, index) => {
    try {
      const title = card.querySelector('.jobTitle, h2.title')?.textContent?.trim();
      const company = card.querySelector('.companyName, .company')?.textContent?.trim();
      const location = card.querySelector('.companyLocation, .location')?.textContent?.trim();
      const description = card.querySelector('.job-snippet, .summary')?.textContent?.trim();
      const jobId = card.getAttribute('data-jk') || `indeed-${Date.now()}-${index}`;
      
      // Also try to extract salary from the card
      const salary = card.querySelector('.salary-snippet, .salaryText, .metadata.salary-snippet-container')?.textContent?.trim();

      if (title && company) {
        jobs.push({
          id: `indeed-${jobId}`,
          title: title,
          company: company,
          location: location || '',
          description: description || '',
          type: '',
          experience: '',
          salary: salary || '',
          contactEmail: '',
          company_website: '',
          adaRequirements: [],
          skills: [],
          industry: '',
          posted_date: new Date().toISOString().split('T')[0],
          source: 'Indeed',
          apply_url: `https://www.indeed.com/viewjob?jk=${jobId}`
        });
      }
    } catch (e) {
      console.warn('Failed to parse job card:', e);
    }
  });
  
  return jobs;
};

/**
 * Parse LinkedIn job listings
 */
const parseLinkedIn = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const jobs = [];
  
  // LinkedIn job cards
  const jobCards = doc.querySelectorAll('.job-search-card, .jobs-search__results-list li');
  
  jobCards.forEach((card, index) => {
    try {
      const title = card.querySelector('.job-search-card__title, h3')?.textContent?.trim();
      const company = card.querySelector('.job-search-card__company-name, .company-name')?.textContent?.trim();
      const location = card.querySelector('.job-search-card__location, .job-location')?.textContent?.trim();
      const link = card.querySelector('a')?.href;
      
      if (title && company) {
        jobs.push({
          id: `linkedin-${Date.now()}-${index}`,
          title: title,
          company: company,
          location: location || '',
          description: '',
          type: '',
          experience: '',
          salary: '',
          contactEmail: '',
          company_website: '',
          adaRequirements: [],
          skills: [],
          industry: '',
          posted_date: new Date().toISOString().split('T')[0],
          source: 'LinkedIn',
          apply_url: link || ''
        });
      }
    } catch (e) {
      console.warn('Failed to parse LinkedIn job:', e);
    }
  });
  
  return jobs;
};

/**
 * Parse USAJobs listings
 */
const parseUSAJobs = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const jobs = [];
  
  const jobCards = doc.querySelectorAll('.usajobs-search-result, [data-item]');
  
  jobCards.forEach((card, index) => {
    try {
      const title = card.querySelector('.usajobs-search-result--core__title, h2')?.textContent?.trim();
      const agency = card.querySelector('.usajobs-search-result--core__agency, .agency')?.textContent?.trim();
      const location = card.querySelector('.usajobs-search-result--core__location, .location')?.textContent?.trim();
      
      // Try to extract salary/grade from the card
      const salaryEl = card.querySelector('.usajobs-search-result--core__salary, .salary')?.textContent?.trim();

      if (title && agency) {
        jobs.push({
          id: `usajobs-${Date.now()}-${index}`,
          title: title,
          company: agency,
          location: location || '',
          description: '',
          type: '',
          experience: '',
          salary: salaryEl || '',
          contactEmail: '',
          company_website: '',
          adaRequirements: [],
          skills: [],
          industry: 'Government',
          posted_date: new Date().toISOString().split('T')[0],
          source: 'USAJobs',
          apply_url: 'https://www.usajobs.gov'
        });
      }
    } catch (e) {
      console.warn('Failed to parse USAJobs listing:', e);
    }
  });
  
  return jobs;
};

/**
 * Crawl jobs from a specific source
 */
export const crawlJobSource = async (sourceName, country, options = {}) => {
  const source = JOB_SOURCES[sourceName];
  if (!source) {
    throw new Error(`Unknown job source: ${sourceName}`);
  }
  
  console.log(`🔍 Crawling ${source.name} for country: ${country}...`);
  
  try {
    const targetUrl = buildCrawlUrl(source.baseUrl, sourceName, country, options);
    const { html, proxy } = await fetchViaProxy(targetUrl);
    console.log(`📄 Fetched HTML from ${source.name} via ${proxy}`);
    
    // Parse based on source
    let jobs = [];
    if (sourceName === 'indeed') {
      jobs = parseIndeed(html);
    } else if (sourceName === 'linkedin') {
      jobs = parseLinkedIn(html);
    } else if (sourceName === 'usajobs') {
      jobs = parseUSAJobs(html);
    }
    
    // Attach any additional skill tags from options
    if (options.skills && Array.isArray(options.skills) && options.skills.length) {
      jobs = jobs.map(job => {
        const combined = Array.from(new Set([...(job.skills || []), ...options.skills]));
        return { ...job, skills: combined };
      });
    }

    console.log(`✅ Parsed ${jobs.length} jobs from ${source.name} (country: ${country})`);
    return jobs;
  } catch (error) {
    console.error(`❌ Failed to crawl ${source.name} for country ${country}:`, error);
    throw error;
  }
};

/**
 * Crawl all job sources — 3 Real API sources first, then HTML scraping
 * Returns ONLY real data. No synthetic/fabricated jobs.
 */
export const crawlAllSources = async (onProgress, options = {}) => {
  const allJobs = [];
  const htmlSources = Object.keys(JOB_SOURCES);
  const countries = options.country ? (Array.isArray(options.country) ? options.country : [options.country]) : ['USA'];

  console.log(`🌍 Starting REAL crawl with countries:`, countries);
  console.log(`🏷️ Skills filter:`, options.skills);

  // 3 API sources + 3 HTML sources per country
  const apiSources = [
    { name: 'Remotive API', fn: crawlRemotiveAPI },
    { name: 'The Muse API', fn: crawlTheMuseAPI },
    { name: 'Arbeitnow API', fn: crawlArbeitnowAPI }
  ];

  let step = 0;
  const totalSteps = countries.length * (apiSources.length + htmlSources.length);

  // ===== Phase 1: Real JSON API Sources =====
  for (const country of countries) {
    for (const api of apiSources) {
      step += 1;
      if (onProgress) {
        onProgress({ current: step, total: totalSteps, source: `${api.name} (Real Jobs)`, country, status: 'crawling' });
      }
      try {
        const apiJobs = await api.fn(country, options);
        console.log(`✅ ${api.name}: ${apiJobs.length} real jobs for ${country}`);
        allJobs.push(...apiJobs);
        if (onProgress) {
          onProgress({ current: step, total: totalSteps, source: `${api.name} (Real Jobs)`, country, status: 'completed', jobsFound: apiJobs.length });
        }
      } catch (error) {
        console.error(`❌ ${api.name} failed for ${country}:`, error.message);
        if (onProgress) {
          onProgress({ current: step, total: totalSteps, source: `${api.name} (Real Jobs)`, country, status: 'failed', error: error.message });
        }
      }
      await new Promise(r => setTimeout(r, 500));
    }
  }

  // ===== Phase 2: HTML Scraping Sources (Indeed, LinkedIn, USAJobs — usually blocked by CORS) =====
  for (const country of countries) {
    for (const sourceName of htmlSources) {
      step += 1;
      if (onProgress) {
        onProgress({ current: step, total: totalSteps, source: JOB_SOURCES[sourceName].name, country, status: 'crawling' });
      }
      try {
        const jobs = await crawlJobSource(sourceName, country, options);
        console.log(`✅ ${JOB_SOURCES[sourceName].name}: ${jobs.length} jobs (${country})`);
        allJobs.push(...jobs);
        if (onProgress) {
          onProgress({ current: step, total: totalSteps, source: JOB_SOURCES[sourceName].name, country, status: 'completed', jobsFound: jobs.length });
        }
        await new Promise(r => setTimeout(r, 2000));
      } catch (error) {
        console.error(`Failed to crawl ${sourceName} for ${country}:`, error.message);
        if (onProgress) {
          onProgress({ current: step, total: totalSteps, source: JOB_SOURCES[sourceName].name, country, status: 'failed', error: error.message });
        }
      }
    }
  }

  // ===== Deduplicate by job ID =====
  const deduped = new Map();
  allJobs.forEach(j => { if (j.id) deduped.set(j.id, j); else deduped.set(`noid-${Math.random()}`, j); });
  let finalJobs = [...deduped.values()];

  // ===== Attach additional skill tags from options =====
  if (options.skills && Array.isArray(options.skills) && options.skills.length) {
    finalJobs = finalJobs.map(job => ({
      ...job,
      skills: Array.from(new Set([...(job.skills || []), ...options.skills]))
    }));
  }

  console.log(`✅ Crawl complete! ${finalJobs.length} REAL jobs from ${countries.length} countries (via ${apiSources.length} APIs + ${htmlSources.length} HTML sources)`);
  return finalJobs.map(normalizeJob);
};

/**
 * Save jobs to GitHub
 */
export const saveJobsToGitHub = async (jobs) => {
  const GITHUB_CONFIG = {
    owner: 'Nibyor2025',
    repo: 'newsletter',
    token: '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'
  };
  
  const filePath = 'data/jobs.json';
  
  try {
    // Get current file SHA
    const getResponse = await fetch(
      `https://api.github.com/repos/${GITHUB_CONFIG.owner}/${GITHUB_CONFIG.repo}/contents/${filePath}`,
      {
        headers: {
          'Authorization': `token ${GITHUB_CONFIG.token}`,
          'Accept': 'application/vnd.github.v3+json'
        }
      }
    );
    
    let sha = null;
    if (getResponse.ok) {
      const data = await getResponse.json();
      sha = data.sha;
    }
    
    // Prepare content
    const content = btoa(unescape(encodeURIComponent(JSON.stringify(jobs, null, 2))));
    
    // Update file
    const updateResponse = await fetch(
      `https://api.github.com/repos/${GITHUB_CONFIG.owner}/${GITHUB_CONFIG.repo}/contents/${filePath}`,
      {
        method: 'PUT',
        headers: {
          'Authorization': `token ${GITHUB_CONFIG.token}`,
          'Accept': 'application/vnd.github.v3+json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: `Update jobs: ${jobs.length} listings (${new Date().toISOString()})`,
          content: content,
          sha: sha,
          branch: 'main'
        })
      }
    );
    
    if (!updateResponse.ok) {
      const error = await updateResponse.json();
      throw new Error(`GitHub API error: ${error.message}`);
    }
    
    console.log(`✅ Saved ${jobs.length} jobs to GitHub`);
    return { success: true, count: jobs.length };
  } catch (error) {
    console.error('❌ Failed to save to GitHub:', error);
    throw error;
  }
};

/**
 * @deprecated — No synthetic data generation. Returns empty array.
 * All data must come from real API/crawl sources.
 */
export const generateSyntheticJobs = (count = 0, options = {}) => {
  console.warn('⚠️ generateSyntheticJobs is disabled — only real job data is used');
  return [];
};

/**
 * Normalize a crawler job object so that BOTH the crawler field names
 * and the consumer-expected field names are present on every job.
 * This avoids "undefined" in filters, cards, exports, etc.
 */
export const normalizeJob = (job) => {
  if (!job) return job;
  return {
    ...job,
    // Consumer-expected aliases  ← crawler originals
    jobTitle:         job.jobTitle         || job.title            || '',
    title:            job.title            || job.jobTitle         || '',
    level:            job.level            || job.experience       || '',
    experience:       job.experience       || job.level            || '',
    employmentType:   job.employmentType   || job.type             || '',
    type:             job.type             || job.employmentType   || '',
    postedDate:       job.postedDate       || job.posted_date      || '',
    posted_date:      job.posted_date      || job.postedDate       || '',
    preferredSkills:  job.preferredSkills  || job.skills           || [],
    skills:           job.skills           || job.preferredSkills  || [],
    companyWebsite:   job.companyWebsite   || job.company_website  || '',
    company_website:  job.company_website  || job.companyWebsite   || '',
    applicationUrl:   job.applicationUrl   || job.apply_url        || '',
    apply_url:        job.apply_url        || job.applicationUrl   || '',
    status:           job.status           || 'active',
    // Ensure other common fields have safe defaults
    company:          job.company          || '',
    location:         job.location         || '',
    description:      job.description      || '',
    salary:           job.salary           || '',
    contactEmail:     job.contactEmail     || '',
    contactPhone:     job.contactPhone     || '',
    industry:         job.industry         || '',
    adaRequirements:  job.adaRequirements  || [],
    source:           job.source           || 'Unknown',
  };
};

export default {
  crawlJobSource,
  crawlAllSources,
  saveJobsToGitHub,
  generateSyntheticJobs,
  normalizeJob
};
